import { Completion } from '@codemirror/autocomplete'
import { InfoRounded } from '@mui/icons-material'
import { Stack, Typography } from '@mui/material'
import { memo, useCallback } from 'react'
import { INode, INodeReportPlaceholder } from '../../../../../definitions/Nodes'
import { IShapeInput } from '../../../../../managers/NodeManager'
import DatasetPlaceholder from './DatasetPlaceholder'
import TextPlaceholder from './TextPlaceholder'

interface PlaceholderWithIndex {
    index: number
    placeholder: INodeReportPlaceholder
}

interface P {
    contents: { name: string, contents: string[] }[]
    textPlaceholders: PlaceholderWithIndex[]
    datasetPlaceholders: PlaceholderWithIndex[]
    setSelected: React.Dispatch<React.SetStateAction<INode<'out_report'>>>
    disable: boolean
    shapes: IShapeInput[]
    globalsSuggestions: Completion[]
}

const Placeholders = ({ contents, textPlaceholders, datasetPlaceholders, setSelected, disable, shapes, globalsSuggestions }: P) => {
    const updatePlaceholderAt = useCallback((index: number, updater: (p: INodeReportPlaceholder) => INodeReportPlaceholder) => {
        setSelected(prev => {
            const newPlaceholders = prev.data.reportData.placeholders.map((p, i) => i === index ? updater(p) : p)
            return {
                ...prev,
                data: {
                    ...prev.data,
                    reportData: { ...prev.data.reportData, placeholders: newPlaceholders }
                }
            }
        })
    }, [setSelected])

    const onTextPlaceholderChange = useCallback((newValue: string, placeholderIdx: number) => {
        updatePlaceholderAt(placeholderIdx, p => ({
            ...p,
            referenceData: newValue
        }))
    }, [updatePlaceholderAt])

    const onDatasetPlaceholderChange = useCallback((newValue: string, placeholderIdx: number) => {
        updatePlaceholderAt(placeholderIdx, p => ({
            ...p,
            referenceData: newValue,
            columns: p.columns?.map(c => ({ ...c, value: '' })) ?? []
        }))
    }, [updatePlaceholderAt])

    const onDatasetPlaceholderConditionClear = useCallback((placeholderIdx: number) => {
        updatePlaceholderAt(placeholderIdx, p => ({
            ...p,
            condition: { key: '', operator: '=', value: '' }
        }))
    }, [updatePlaceholderAt])

    const onDatasetPlaceholderConditionChange = useCallback((placeholderIdx: number, keyInput: string, operatorInput: '=' | '!=' | '>' | '<' | '>=' | '<=' | 'includes', valueInput: string) => {
        updatePlaceholderAt(placeholderIdx, p => ({
            ...p,
            condition: { key: keyInput, operator: operatorInput, value: valueInput }
        }))
    }, [updatePlaceholderAt])

    const onColumnPlaceholderChange = useCallback((placeholderIdx: number, columnIdx: number, val: string) => {
        updatePlaceholderAt(placeholderIdx, p => ({
            ...p,
            columns: p.columns?.map((c, i) =>
                i === columnIdx ? { ...c, value: val } : c
            ) ?? []
        }))
    }, [updatePlaceholderAt])

    return (
        <>
            <Stack spacing={1}>
                <Stack flexDirection='row' gap={1}>
                    <Typography variant='subtitle2'>Text placeholders</Typography>
                    <InfoRounded fontSize='small' />
                </Stack>
                <Stack spacing={1}>
                    {textPlaceholders.map(({ index, placeholder }) => (
                        <MemoTextPlaceholder
                            key={`text-${placeholder.name}-${index}`}
                            contents={contents}
                            placeholder={placeholder}
                            placeholderIdx={index}
                            disable={disable}
                            onTextPlaceholderChange={onTextPlaceholderChange}
                            shapes={shapes}
                            globalsSuggestions={globalsSuggestions}
                        />
                    ))}
                </Stack>
            </Stack>

            <Stack spacing={1}>
                <Stack flexDirection='row' gap={1}>
                    <Typography variant='subtitle2'>Dataset placeholders</Typography>
                    <InfoRounded fontSize='small' />
                </Stack>
                <Stack spacing={1}>
                    {datasetPlaceholders.map(({ index, placeholder }) => (
                        <MemoDatasetPlaceholder
                            key={`dataset-${placeholder.name}-${index}`}
                            contents={contents}
                            placeholder={placeholder}
                            placeholderIdx={index}
                            disable={disable}
                            onDatasetPlaceholderChange={onDatasetPlaceholderChange}
                            onDatasetPlaceholderConditionClear={onDatasetPlaceholderConditionClear}
                            onDatasetPlaceholderConditionChange={onDatasetPlaceholderConditionChange}
                            onColumnPlaceholderChange={onColumnPlaceholderChange}
                            shapes={shapes}
                            globalsSuggestions={globalsSuggestions}
                        />
                    ))}
                </Stack>
            </Stack>
        </>
    )
}

const MemoTextPlaceholder = memo(TextPlaceholder, (prev, next) => {
    return (
        prev.placeholder === next.placeholder
    )
})

const MemoDatasetPlaceholder = memo(DatasetPlaceholder, (prev, next) => {
    return (
        prev.placeholder === next.placeholder
    )
})

export default memo(Placeholders)
